"use client";

import { Button } from "@/components/atoms/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  leadCtaClick,
  tabSwitched,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { RootState } from "@/store/store";

// Backend data structure
export interface SemesterBackend {
  id?: number;
  program_structure_id?: number;
  name: string;
  name_color?: string;
  section_color?: string;
  selected_section_color?: string;
  selected_section_bar_color?: string;
  header_text_1?: string;
  header_text_1_color?: string;
  header_text_2?: string;
  header_text_2_color?: string;
  header_section_color?: string;
  subject_container_color?: string;
  priority: number;
  status?: number;
  is_deleted?: number;
  subjects?: Array<{
    id?: number;
    semester_id?: number;
    subject_name: string;
    subject_name_text_color?: string;
    /** Optional in the CMS — null when the subject carries no credits. */
    credits?: number | string | null;
    credits_text_color?: string;
    priority?: number;
    status?: number;
    is_deleted?: number;
  }>;
}

// Frontend data structure
export interface Course {
  name: string;
  /** null when the subject has no credits; 0 is a real value and still renders. */
  credits: number | null;
  nameColor?: string;
  creditsColor?: string;
}

export interface SemesterData {
  id: number;
  name: string;
  nameColor?: string;
  sectionColor?: string;
  selectedSectionColor?: string;
  selectedSectionBarColor?: string;
  headerText1?: string;
  headerText1Color?: string;
  headerText2?: string;
  headerText2Color?: string;
  headerSectionColor?: string;
  subjectContainerColor?: string;
  courses: Course[];
  /** False when no subject in this semester has credits — the column is dropped. */
  showCredits: boolean;
}

interface ProgramStructure1Props {
  data: {
    data: {
      title?: string;
      title_text_color?: string;
      image?: string;
      alt_image?: string;
      container_background_color?: string;
      // CTA button fields
      cta_button_text?: string;
      cta_button_text_color?: string;
      cta_button_color?: string;
      cta_button_icon?: string;
      cta_button_icon_alt?: string;
      // Global table styling (overrides per-semester colors)
      header_section_color?: string;
      subject_container_color?: string;
      semesters?: SemesterBackend[];
    };
  };
  className?: string;
}

/**
 * Credits are optional per subject. Everything that means "not set" — null, undefined,
 * an empty/whitespace string, or a non-numeric value — collapses to null so the cell is
 * left blank. A real 0 typed by an editor survives and still renders as "0".
 */
const toCredits = (raw: number | string | null | undefined): number | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

// Transform backend data to frontend format
export const transformSemesterData = (
  semesters: SemesterBackend[],
): SemesterData[] => {
  return semesters
    .filter((semester) => semester.status === 1) // Filter by status first
    .sort((a, b) => (a.priority || 0) - (b.priority || 0)) // Sort semesters by priority
    .map((semester) => {
      const subjects = semester.subjects || [];

      // Sort subjects by priority and filter by status
      const sortedSubjects = [...subjects]
        .filter((subject) => subject.status === 1)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .map((subject) => ({
          name: subject.subject_name,
          credits: toCredits(subject.credits),
          nameColor: subject.subject_name_text_color,
          creditsColor: subject.credits_text_color,
        }));

      return {
        id: semester.id || 0,
        name: semester.name,
        nameColor: semester.name_color,
        sectionColor: semester.section_color,
        selectedSectionColor: semester.selected_section_color,
        selectedSectionBarColor: semester.selected_section_bar_color,
        headerText1: semester.header_text_1,
        headerText1Color: semester.header_text_1_color,
        headerText2: semester.header_text_2,
        headerText2Color: semester.header_text_2_color,
        headerSectionColor: semester.header_section_color,
        subjectContainerColor: semester.subject_container_color,
        courses: sortedSubjects,
        // Credits are optional: keep the column only when at least one subject in this
        // semester has a value, so a credit-less semester renders as a plain subject
        // list instead of a column of empty cells under a "Credits" header.
        showCredits: sortedSubjects.some((course) => course.credits !== null),
      };
    });
};

function ProgramStructure1({ data, className }: ProgramStructure1Props) {
  const isMobile = useIsMobile(640);
  const ref = useRef<HTMLElement>(null);
  const previousSemesterIdRef = useRef<number>(0);
  const dispatch = useDispatch();
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  // Extract data from nested structure
  const programData = data?.data || {};

  const {
    title = "",
    title_text_color: titleColor = "#000000",
    image,
    alt_image: altImage = "",
    container_background_color: containerBgColor = "#FFFFFF",
    // CTA button
    cta_button_text: ctaButtonText = "",
    cta_button_text_color: ctaButtonTextColor = "#FFFFFF",
    cta_button_color: ctaButtonColor = "#000000",
    cta_button_icon: ctaButtonIcon = "",
    cta_button_icon_alt: ctaButtonIconAlt = "",
    // Global table styling (overrides per-semester colors if provided)
    header_section_color: globalHeaderSectionColor,
    subject_container_color: globalSubjectContainerColor,
  } = programData;

  // Transform and filter semesters
  const semestersToUse = programData?.semesters || [];
  const transformedSemesters = transformSemesterData(semestersToUse);

  // Filter out semesters without subjects
  const semesters = transformedSemesters.filter(
    (semester) => semester.courses.length > 0,
  );

  const [selectedSemesterId, setSelectedSemesterId] = useState(
    semesters.length > 0 ? semesters[0].id : 0,
  );

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_structure_section");
  //   }
  // }, []);

  // Initialize previousSemesterIdRef when component mounts
  useEffect(() => {
    if (semesters.length > 0 && selectedSemesterId !== 0) {
      previousSemesterIdRef.current = selectedSemesterId;
    }
  }, [semesters.length, selectedSemesterId]);

  // Track semester switching
  const handleSemesterChange = useCallback(
    (semesterId: number) => {
      // Only track if semester actually changed (not initial render)
      if (
        previousSemesterIdRef.current !== semesterId &&
        previousSemesterIdRef.current !== 0
      ) {
        const selectedSemester = semesters.find((sem) => sem.id === semesterId);
        tabSwitched({
          section: "program_structure_section",
          component: "semester_tabs",
          label: selectedSemester?.name || `semester_${semesterId}`,
        });
      }
      previousSemesterIdRef.current = semesterId;
      setSelectedSemesterId(semesterId);
    },
    [semesters],
  );

  // Handle CTA button click with tracking
  const handleOpenLeadForm = useCallback(() => {
    leadCtaClick({
      section: "program_structure_section",
      component: "cta_button",
      label: ctaButtonText,
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureUrl,
      }),
    );
  }, [dispatch, ctaButtonText]);

  const currentSemesterData: SemesterData | undefined = semesters.find(
    (s: SemesterData) => s.id === selectedSemesterId,
  );

  // Don't render if no semesters have subjects

  return (
    <section
      ref={ref}
      className={cn(
        "flex justify-center items-center w-full",
        "px-5 py-8 sm:px-8 sm:py-11",
        className,
      )}
      style={{ backgroundColor: containerBgColor }}
    >
      <div
        className={cn(
          "flex flex-col sm:flex-row justify-center items-center sm:items-start w-full",
          "gap-6 sm:gap-14",
          "sm:w-[1008px] md:w-[1158px] lg:w-[1358px] xl:w-[1678px]",
        )}
      >
        {image && (
          <div
            className={cn(
              "relative rounded-2xl overflow-hidden shadow-xl shrink-0",
              "w-[156px] h-[188px]",
              "sm:w-[312px] sm:h-[362px]",
              "md:w-[356px] md:h-[412px]",
              "lg:w-[356px] lg:h-[412px]",
              "xl:w-[460px] xl:h-[532px]",
            )}
          >
            <Image
              src={image || ""}
              alt={altImage}
              fill
              className="object-cover"
              priority
            />
          </div>
        )}

        <div
          className={cn(
            "flex flex-col gap-6 w-full items-center justify-center sm:items-start flex-1",
          )}
        >
          <div className="flex flex-col gap-3 sm:flex-row justify-between items-center sm:justify-between sm:items-start w-full">
            {title && (
              <h2
                className={cn(
                  "text-heading-6-semibold text-center sm:text-left break-words w-full",
                  "sm:text-heading-5-semibold",
                  "md:text-heading-4-semibold",
                  "lg:text-new-heading-34-semibold",
                  "xl:text-new-heading-42-semibold",
                  "line-clamp-3 sm:line-clamp-2",
                  "type-h3",
                )}
                style={{ color: titleColor }}
              >
                {title}
              </h2>
            )}
            {ctaButtonText && (
              <Button
                className={cn(
                  "text-heading-9-semibold flex gap-[6px] ",
                  "sm:text-heading-9-semibold sm:gap-[6px] sm:px-[14px] sm:py-[6px]",
                  "md:text-heading-7-semibold md:gap-[6px] md:px-[16px] md:py-[6px]",
                  "lg:text-heading-6-semibold lg:gap-[10px] lg:py-2 lg:px-6 ",
                  "xl:text-heading-4-semibold xl:gap-[10px] xl:py-2 xl:px-6 ",
                  "type=h6",
                )}
                style={{
                  backgroundColor: ctaButtonColor,
                  color: ctaButtonTextColor,
                }}
                onClick={handleOpenLeadForm}
              >
                {ctaButtonText}
                {ctaButtonIcon && (
                  <div
                    className={cn(
                      "relative size-[18px]",
                      "sm:size-[26px]",
                      "md:size-8",
                      "lg:size-9",
                      "xl:size-9",
                      "type-h6",
                    )}
                  >
                    <Image src={ctaButtonIcon} alt={ctaButtonIconAlt} fill />
                  </div>
                )}
              </Button>
            )}
          </div>
          {/* Right Side - Main Table */}
          <div
            className={cn(
              "flex flex-col gap-3 sm:flex-row  w-full justify-start overflow-hidden rounded-[10px] border border-gray-200",
              "xs:p-[10px]",
              "sm:p-6  ",
              "md:p-6",
              "lg:p-6",
              "xl:p-12",
            )}
            style={{ backgroundColor: globalSubjectContainerColor }}
          >
            <div className="flex flex-row sm:flex-col gap-0 w-full sm:w-auto overflow-x-auto sm:overflow-x-visible  justify-start sm:mx-0">
              {semesters &&
                semesters.length > 0 &&
                semesters?.map((sem) => {
                  const isSelected = selectedSemesterId === sem.id;
                  return (
                    <button
                      key={sem.id}
                      onClick={() => handleSemesterChange(sem.id)}
                      className={cn(
                        "px-4 py-3 sm:px-6 whitespace-nowrap transition-all relative flex-shrink-0",
                        "xs:py-4 xs:px-[20px] ",
                        "text-heading-9-semibold",
                        "sm:text-heading-8-semibold",
                        "md:text-heading-7-semibold",
                        "lg:text-heading-7-semibold",
                        "xl:text-heading-6-semibold xl:px-8 xl:py-[20px] type-h6",
                      )}
                      style={{
                        backgroundColor: isSelected
                          ? sem.selectedSectionColor || "#E8E8E8"
                          : sem.sectionColor || "#F5F5F5",
                        color:
                          sem.nameColor ||
                          (isSelected ? "#000000" : "rgba(0, 0, 0, 0.7)"),
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    >
                      {isSelected && (
                        <div
                          className="absolute left-0 top-0 bottom-0 w-1 rounded-r"
                          style={{
                            backgroundColor:
                              sem.selectedSectionBarColor || "#BE2330",
                          }}
                        />
                      )}
                      {sem.name}
                    </button>
                  );
                })}
            </div>
            <div className={cn("overflow-x-auto smooth-scroll w-full")}>
              <Table className="min-w-full rounded-2xl border-collapse">
                <TableHeader
                  style={{
                    backgroundColor:
                      globalHeaderSectionColor ||
                      currentSemesterData?.headerSectionColor ||
                      "#BE2330",
                  }}
                >
                  <TableRow
                    className={cn(
                      "border-0 hover:bg-transparent",
                      "xs:rounded-[6px]",
                    )}
                  >
                    <TableHead
                      className={cn(
                        "rounded-tl-4 break-words border-0",
                        "xl:text-heading-6-semibold   md:text-heading-8-semibold",
                        "xs:px-[14px] xs:py-[12px]",

                        "xl:pl-[14px] xl:py-6 ",
                        "type-h6",
                      )}
                      style={{
                        color:
                          currentSemesterData?.headerText1Color || "#FFFFFF",
                      }}
                    >
                      {currentSemesterData?.headerText1 ||
                        currentSemesterData?.name}
                    </TableHead>
                    {/* Credits column is dropped entirely when no subject in the
                        semester has credits — see SemesterData.showCredits. */}
                    {currentSemesterData?.showCredits && (
                      <TableHead
                        className={cn(
                          "text-right rounded-tr-4 whitespace-nowrap border-0",
                          "w-20 sm:w-24",
                          "sm:pr-6",
                          "text-heading-9-semibold",
                          "md:text-heading-8-semibold",
                          "xl:pr-6 xl:py-6",
                          "xl:text-heading-6-semibold",
                          "type-h6",
                        )}
                        style={{
                          color:
                            currentSemesterData?.headerText2Color || "#FFFFFF",
                        }}
                      >
                        {currentSemesterData?.headerText2}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr]:border-0 [&_td]:border-0">
                  {currentSemesterData?.courses?.map((course, index) => (
                    <TableRow
                      key={index}
                      className={cn(
                        "text-start hover:bg-transparent border-0 border-none",
                      )}
                    >
                      <TableCell
                        className={cn(
                          "text-heading-9-regular break-words border-0",
                          "md:text-heading-8-regular",
                          "xl:text-new-body-24-regular",
                          "max-w-[200px] sm:max-w-none",
                          "xs:px-[14px] xs:py-[10px] ",
                          "xl:pl-[14px] xl:py-6 type-body-sm ",
                        )}
                        style={{
                          color: course?.nameColor || "rgba(0, 0, 0, 0.8)",
                        }}
                      >
                        {index + 1}. {course?.name}
                      </TableCell>
                      {/* Blank cell for a subject with no credits in a semester where
                          other subjects do have them — keeps rows aligned. */}
                      {currentSemesterData?.showCredits && (
                        <TableCell
                          className={cn(
                            "text-heading-9-regular text-center whitespace-nowrap border-0",
                            "xs:text-heading-9-semibold",
                            "sm:text-heading-9-semibold",
                            "md:text-heading-8-semibold",
                            "xl:text-heading-6-semibold",
                            " w-20 sm:w-24",
                            "xl:pr-[14px] xl:py-6  type-h6",
                          )}
                          style={{
                            color: course?.creditsColor || "rgba(0, 0, 0, 0.8)",
                          }}
                        >
                          {course?.credits ?? ""}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProgramStructure1;
